import { indentLess, indentMore } from "@codemirror/commands";
import { jsonLanguage } from "@codemirror/lang-json";
import {
  diagnosticCount,
  forceLinting,
  linter,
  nextDiagnostic,
} from "@codemirror/lint";
// Type-only — a value import from @codemirror/state must ride the
// @uiw/react-codemirror re-exports (Vite optimize-dep gotcha, see the
// 07-23 changes file); types are erased and safe.
import type { EditorState } from "@codemirror/state";
import type { ViewUpdate } from "@codemirror/view";
import { EditorView } from "@codemirror/view";
import { designVariables } from "@studio3t/design-variables";
import {
  ButtonSmallSubtleIcon,
  GenericDropdown,
  Icon,
  IconSVG,
  Tooltip,
} from "@studio3t/generic-components";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import {
  type MutableRefObject,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, {
  type DefaultTheme,
  keyframes,
  useTheme,
} from "styled-components";
import {
  buildExtensions,
  CONTEXT_MENU_HEIGHT,
  editorRegionFocusRing,
} from "@/components/JsonViewer";
import { OverlayScroll } from "@/components/OverlayScroll";
import { ErrorStatusButton, jumpToOffset } from "./EditorErrorsPopover";
import { type EditorErrorRow, errorRows } from "./editorErrors";
import { emptySlotHints } from "./emptySlotHintsView";
import {
  buildContextItems,
  CONTENT_PAD_TOP,
  relintOnDocSwap,
  SampleLink,
  useEditorCommands,
  viewOnlyContextItems,
} from "./JsonDocumentEditor";
import { buildPipelineCode, shownPipelineCode } from "./pipelineCode";
import { pipelineCompletion } from "./pipelineCompletion";
import type { LintStatus } from "./pipelineLint";
import {
  erroredStageRoles,
  pipelineLinter,
  stageIdAtPosition,
  statusTrackingLinter,
} from "./pipelineLint";
import { usePipelineStaging } from "./pipelineStaging";
import { PIPELINE_CODE_TEMPLATE, TEMPLATE_SAMPLE } from "./pipelineTemplate";
import { useStudioPipeline } from "./StudioPipelineProvider";
import { type StageProgress, stageProgressFromCode } from "./stageProgress";
import { useEditorFocusRequest } from "./useEditorFocusRequest";
import { viewOnlyExtensions } from "./viewOnlyEditor";

// Same Editor/StatusBar metrics as the input/script editors.
const STATUS_PADDING = 5;
const STATUS_GAP = 4;

// Whole-document pastes land already pretty-printed: the clipboard filter
// rewrites the pasted text before CodeMirror even builds the transaction,
// so the document is never seen unformatted — one transaction, one undo
// step, no flash on either of CM's paste paths (direct or the hidden-
// textarea fallback). Only when the selection covers the whole doc
// (select-all + paste, or an empty editor); non-JSON pastes go through
// verbatim.
function isWholeDocSelection(state: EditorState): boolean {
  if (state.selection.ranges.length !== 1) return false;
  const selection = state.selection.main;
  if (selection.from !== 0) return false;
  return selection.to === state.doc.length;
}

function prettyPrintJson(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

const formatPasteFilter = EditorView.clipboardInputFilter.of((text, state) =>
  isWholeDocSelection(state) ? prettyPrintJson(text) : text,
);

// The text of a paste that replaced the whole document (select-all + paste,
// or pasting into an empty editor), or null for any other update.
function wholeDocPasteText(u: ViewUpdate): string | null {
  const paste = u.transactions.find((tr) => tr.isUserEvent("input.paste"));
  if (!paste) return null;
  let coversWholeDoc = false;
  const oldLength = paste.startState.doc.length;
  paste.changes.iterChangedRanges((fromA, toA) => {
    if (fromA === 0 && toA === oldLength) coversWholeDoc = true;
  });
  return coversWholeDoc ? u.state.doc.toString() : null;
}

interface UpdateHandlers {
  onEdit: () => void;
  onCursor: (cursor: { line: number; col: number }) => void;
  onHasSelection: (hasSelection: boolean) => void;
  onWholeDocPaste: (text: string) => void;
  onCursorStage: (stageId: string | null) => void;
  pointerDown: () => boolean;
}

// The editor's update listener: lint-pending flips, the cursor readout, the
// whole-document paste reset and the cursor→card focus (see the callers'
// comments for the why of each rule).
function makeUpdateListener(handlers: UpdateHandlers) {
  return EditorView.updateListener.of((u) => {
    if (!u.selectionSet && !u.docChanged) return;
    // The linter only re-runs after its idle delay — flip to pending
    // right away so an edit never shows the previous verdict.
    if (u.docChanged) handlers.onEdit();
    const main = u.state.selection.main;
    const line = u.state.doc.lineAt(main.head);
    handlers.onCursor({ line: line.number, col: main.head - line.from + 1 });
    // Selection presence for the context menu's Cut/Copy.
    handlers.onHasSelection(!main.empty);
    // A paste that replaces the whole document restarts the graph from the
    // whole-pipeline overview instead of zooming anywhere; smaller pastes
    // keep the normal cursor focus. The reset carries the pasted text: the
    // graph jumps straight to it instead of waiting for the draft state,
    // which may land in a different React batch.
    const pasted = wholeDocPasteText(u);
    if (pasted !== null) {
      handlers.onWholeDocPaste(pasted);
      return;
    }
    // Which stage card the cursor sits on — the graph strip zooms in on it.
    // Only for a plain cursor while no mouse button is down: pressing the
    // button already places an (empty) cursor at the drag anchor, so
    // without the pointer guard starting a text selection would pan/zoom
    // the canvas under the user. Mouse placements are handled on release
    // instead. Same-value sets are free (React bails out).
    if (main.empty && !handlers.pointerDown()) {
      handlers.onCursorStage(stageIdAtPosition(u.state, main.head));
    }
  });
}

// While the mouse button is down in the editor the user may be dragging out
// a selection, and the cursor→card canvas focus stays quiet. A plain click
// commits its focus here on release; a drag that ends in a selection
// commits nothing.
function useFocusCommitOnRelease(
  cmRef: RefObject<ReactCodeMirrorRef | null>,
  pointerDownRef: MutableRefObject<boolean>,
  setFocusedStageId: (id: string | null) => void,
) {
  useEffect(() => {
    const commitFocusOnRelease = () => {
      if (!pointerDownRef.current) return;
      pointerDownRef.current = false;
      // CodeMirror finalizes the mouse selection in its own mouseup
      // listeners, registered after this one — read the settled selection
      // a tick later.
      window.setTimeout(() => {
        const view = cmRef.current?.view;
        if (view) commitCursorFocus(view, setFocusedStageId);
      }, 0);
    };
    window.addEventListener("mouseup", commitFocusOnRelease);
    return () => window.removeEventListener("mouseup", commitFocusOnRelease);
  }, [cmRef, pointerDownRef, setFocusedStageId]);
}

// A settled plain cursor (no selection) focuses its stage card.
function commitCursorFocus(
  view: EditorView,
  setFocusedStageId: (id: string | null) => void,
): void {
  const main = view.state.selection.main;
  if (main.empty) {
    setFocusedStageId(stageIdAtPosition(view.state, main.head));
  }
}

// The validity slot of the status bar: spinner while checks run, then the
// green tick / red error verdict of the full async lint pass.
function LintVerdictIcon({
  status,
}: {
  status: "clean" | "errors" | "placeholders";
}) {
  const theme = useTheme();
  const look = {
    clean: { icon: Icon.Confirm, color: theme.semanticMainPrimary },
    errors: { icon: Icon.Error, color: theme.statusError },
    // Placeholders left: a warning sign, like their gutter marks.
    placeholders: { icon: Icon.Warning, color: theme.statusWarning },
  }[status];
  return (
    <IconSVG icon={look.icon} size={16} colors={{ default: look.color }} />
  );
}

const LINT_TITLES = {
  pending: "Checking pipeline…",
  clean: "Pipeline is valid",
  errors: "Pipeline has problems",
  placeholders: "Pipeline has placeholders to fill in",
} as const;

function LintStatusSlot({
  code,
  lintStatus,
  errors,
  onJump,
}: {
  code: string;
  lintStatus: LintStatus;
  errors: EditorErrorRow[];
  onJump: (from: number) => void;
}) {
  if (lintStatus === "empty") return null;
  if (lintStatus === "errors") {
    return (
      <ErrorStatusButton
        rows={errors}
        title={LINT_TITLES.errors}
        documentName="pipeline code"
        code={code}
        onJump={onJump}
        testIdBase="pipeline-code"
      />
    );
  }
  return (
    <StatusIconSlot title={LINT_TITLES[lintStatus]}>
      {lintStatus === "pending" ? (
        <LintSpinner data-testid="pipeline-lint-pending" />
      ) : (
        <LintVerdictIcon status={lintStatus} />
      )}
    </StatusIconSlot>
  );
}

// The toolbar's "Full pipeline code" view (Figma Bridge_PipelineCode inside
// 13022-10413): the staged sections rendered as /studio's pipeline
// configuration JSON in the shared editor chrome. Editable like /studio's
// pipeline editor — edits live in a context draft and survive view switches;
// a staging change regenerates the code and discards the draft (the staging
// panels stay the source of truth).
// The document under edit: the staged sections' generated code with the
// user's draft resolved over it; edits re-anchor the draft on the current
// generation.
function usePipelineCodeDocument() {
  const {
    sources,
    transforms,
    sinks,
    schemas,
    pipelineCodeDraft,
    setPipelineCodeDraft,
    pipelineExtras,
  } = usePipelineStaging();
  const generated = useMemo(
    () =>
      buildPipelineCode(
        { sources, transforms, sinks, schemas },
        pipelineExtras,
      ),
    [sources, transforms, sinks, schemas, pipelineExtras],
  );
  const { editsLocked, opening } = useStudioPipeline();
  const code = shownPipelineCode(
    opening,
    pipelineCodeDraft,
    generated,
    editsLocked,
  );
  const setCode = useCallback(
    (text: string) => setPipelineCodeDraft({ text, basedOn: generated }),
    [setPipelineCodeDraft, generated],
  );
  return { code, setCode };
}

// "Go to the error" (the switch-to-stages modal): the cursor lands on the
// first lint problem — the parse error's own position.
function useCodeJumpToDiagnostic(cmRef: RefObject<ReactCodeMirrorRef | null>) {
  const { codeJumpRequest } = usePipelineStaging();
  useEffect(() => {
    if (!codeJumpRequest) return;
    const view = cmRef.current?.view;
    if (!view) return;
    const jump = () => {
      view.focus();
      view.dispatch({ selection: { anchor: 0 } });
      nextDiagnostic(view);
    };
    // Lint runs on a typing pause; a jump requested before it ran has no
    // diagnostic to land on yet — force a pass first (the parse error is
    // synchronous within it).
    if (diagnosticCount(view.state) > 0) {
      jump();
      return;
    }
    forceLinting(view);
    const timer = window.setTimeout(jump, 400);
    return () => window.clearTimeout(timer);
  }, [codeJumpRequest, cmRef]);
}

interface PipelineExtensionsOptions {
  theme: DefaultTheme;
  wrap: boolean;
  editsLocked: boolean;
  setLintStatus: (status: LintStatus) => void;
  /** Doc text of the last settled pass (staging context, shared with the
   *  mount reset below). */
  lintedText: { current: string | null };
  setProgress: (progress: StageProgress | null) => void;
  setErrorRows: (rows: EditorErrorRow[]) => void;
  setCursor: (cursor: { line: number; col: number }) => void;
  setHasSelection: (hasSelection: boolean) => void;
  setFocusedStageId: (id: string | null) => void;
  resetGraphView: (text: string) => void;
  pointerDownRef: MutableRefObject<boolean>;
}

// The editor's extensions: the shared JSON viewer chrome plus the pipeline's
// own linting, completion and cursor tracking. Only the theme, the wrap
// preference and the view-only mode rebuild the set — the context setters
// and the pointer ref keep a stable identity across renders.
function usePipelineCodeExtensions(options: PipelineExtensionsOptions) {
  const {
    theme,
    wrap,
    editsLocked,
    setLintStatus,
    lintedText,
    setProgress,
    setErrorRows,
    setCursor,
    setHasSelection,
    setFocusedStageId,
    resetGraphView,
    pointerDownRef,
  } = options;
  return useMemo(
    () => [
      ...buildExtensions(theme, { lineWrapping: wrap }),
      ...viewOnlyExtensions(editsLocked),
      // The ghost template: CodeMirror draws the placeholder over an empty
      // document; it inherits .cm-content's white-space, so the multi-line
      // text keeps its lines. Line-number gray like the input editor's
      // zero state (Figma 14397-167447), never the placeholder token.
      EditorView.theme({
        ".cm-placeholder": { color: theme.editorLineNumTxt },
      }),
      // Squiggles for JSON syntax errors and pipeline-shape problems —
      // /studio's Monaco JSON worker parity plus the CreateJobRequest shape.
      // The settled pass also reads the toolbar's stage progress off the
      // code: a step is done once its entry is filled AND lint-clean —
      // and the status bar's Errors popover lists the same pass.
      linter(
        statusTrackingLinter(
          pipelineLinter,
          setLintStatus,
          (view, diags) => {
            setProgress(
              stageProgressFromCode(
                view.state.doc.toString(),
                erroredStageRoles(view.state, diags),
              ),
            );
            setErrorRows(errorRows(view.state, diags));
          },
          lintedText,
        ),
      ),
      // A workspace load / whole-doc paste swaps the entire document — drop
      // the previous document's diagnostics instead of letting them smear
      // over the new text until the lint delay expires.
      relintOnDocSwap,
      // Semantic autosuggestions: pipeline keys, encodings, live
      // service/resource/schema names (shared lookup cache with the linter).
      jsonLanguage.data.of({ autocomplete: pipelineCompletion }),
      // Ghost hints inside the template's empty "" reference values.
      emptySlotHints(theme),
      formatPasteFilter,
      makeUpdateListener({
        onEdit: () => setLintStatus("pending"),
        onCursor: setCursor,
        onHasSelection: setHasSelection,
        onWholeDocPaste: (text) => {
          // Already pretty-printed by formatPasteFilter.
          resetGraphView(text);
          setFocusedStageId(null);
        },
        onCursorStage: setFocusedStageId,
        pointerDown: () => pointerDownRef.current,
      }),
    ],
    [
      theme,
      wrap,
      editsLocked,
      setLintStatus,
      lintedText,
      setProgress,
      setErrorRows,
      setCursor,
      setHasSelection,
      setFocusedStageId,
      resetGraphView,
      pointerDownRef,
    ],
  );
}

// The scrolling editor region: the CodeMirror surface, the empty editor's
// template link over it, and the context menu — hosted on the editor area
// only, never the status bar (same recipe as the input/schema editors).
function PipelineEditorSurface({
  cmRef,
  pointerDownRef,
  code,
  setCode,
  extensions,
  editsLocked,
  opening,
  contextItems,
}: {
  cmRef: RefObject<ReactCodeMirrorRef>;
  pointerDownRef: MutableRefObject<boolean>;
  code: string;
  setCode: (text: string) => void;
  extensions: ReturnType<typeof usePipelineCodeExtensions>;
  editsLocked: boolean;
  // A pipeline is being opened into this editor: no ghost template and no
  // "Insert template" over the (still) empty document — its code is on
  // its way.
  opening: boolean;
  contextItems: ReturnType<typeof buildContextItems>;
}) {
  const editorAreaRef = useRef<HTMLDivElement>(null);
  return (
    <EditorArea ref={editorAreaRef}>
      <EditorScroll>
        <div
          // Capture phase: CodeMirror places the drag-anchor cursor in its
          // own bubble-phase mousedown handler, which would run first and
          // slip past the pointer guard.
          onMouseDownCapture={() => {
            pointerDownRef.current = true;
          }}
        >
          <CodeMirror
            ref={cmRef}
            value={code}
            onChange={setCode}
            extensions={extensions}
            placeholder={opening ? undefined : PIPELINE_CODE_TEMPLATE}
            // In the zero-point state Tab leaves the editor for the Insert
            // template link below it — there is nothing to indent. With
            // content, Tab indents as usual; Escape then Tab still leaves
            // (CodeMirror's own escape hatch).
            indentWithTab={code !== ""}
            basicSetup={{
              lineNumbers: true,
              foldGutter: false,
              bracketMatching: true,
              closeBrackets: true,
              autocompletion: true,
              highlightActiveLine: false,
              highlightActiveLineGutter: false,
            }}
          />
        </div>
        {code === "" && !editsLocked && !opening && (
          <TemplateActionOverlay>
            <SampleLink
              type="button"
              onClick={() => setCode(TEMPLATE_SAMPLE.content)}
              data-testid="pipeline-insert-template"
            >
              {TEMPLATE_SAMPLE.label}
            </SampleLink>
          </TemplateActionOverlay>
        )}
      </EditorScroll>
      <GenericDropdown
        asContextMenu={editorAreaRef}
        dropdownItems={viewOnlyContextItems(contextItems, editsLocked, code)}
        width={{ strategy: "fixed", size: 220 }}
        height={CONTEXT_MENU_HEIGHT}
      />
    </EditorArea>
  );
}

export function PipelineCodeEditor() {
  const theme = useTheme();
  const {
    pipelineCodeLintStatus: lintStatus,
    setPipelineCodeLintStatus: setLintStatus,
    pipelineCodeLintedText: lintedText,
    pipelineCodeErrors: errors,
    setPipelineCodeErrors: setErrorRows,
    setPipelineCodeProgress: setProgress,
    setFocusedStageId,
    resetGraphView,
  } = usePipelineStaging();
  const cmRef = useRef<ReactCodeMirrorRef>(null);
  const [wrapPref, setWrap] = useState(true);
  // The view-only studio always wraps — with nothing to edit, the toggle
  // (and the footer it lives in) has no job.
  const { editsLocked, opening } = useStudioPipeline();
  const wrap = editsLocked || wrapPref;
  const [cursor, setCursor] = useState({ line: 1, col: 1 });
  const [hasSelection, setHasSelection] = useState(false);
  const pointerDownRef = useRef(false);
  useFocusCommitOnRelease(cmRef, pointerDownRef, setFocusedStageId);

  const { code, setCode } = usePipelineCodeDocument();

  // The lint settle state lives in the staging context so the toolbar can
  // gate Run on it. The last verdict goes stale while this editor is
  // unmounted only when staging changes regenerated the code meanwhile —
  // remounting on the very text the verdict was computed for keeps it, so
  // the header's Unresolved chip doesn't blink on a Stages/Full round
  // trip. A stale one restarts at pending; layout effect, so the stale
  // verdict never paints a frame.
  const codeRef = useRef(code);
  codeRef.current = code;
  useLayoutEffect(() => {
    if (codeRef.current === lintedText.current) return;
    setLintStatus("pending");
    setErrorRows([]);
    setProgress(null);
  }, [lintedText, setLintStatus, setErrorRows, setProgress]);

  // The shared editor commands (format / indent / clipboard) — same set the
  // input and schema editors put on their status bars and context menus.
  const commands = useEditorCommands(cmRef, code, setCode);
  useCodeJumpToDiagnostic(cmRef);
  useEditorFocusRequest(cmRef);
  const onToggleWrap = () => setWrap((prev) => !prev);

  // Rebuilt every render — the handlers close over the live editor ref.
  const contextItems = buildContextItems({
    empty: code === "",
    wrap,
    hasSelection,
    // The template is the one sample: "Insert template" sits in the menu
    // next to the empty-state link (same mechanism as the input editor's
    // sample documents).
    samples: [TEMPLATE_SAMPLE],
    onChange: setCode,
    commands,
    onToggleWrap,
  });

  const extensions = usePipelineCodeExtensions({
    theme,
    wrap,
    editsLocked,
    setLintStatus,
    lintedText,
    setProgress,
    setErrorRows,
    setCursor,
    setHasSelection,
    setFocusedStageId,
    resetGraphView,
    pointerDownRef,
  });

  return (
    <Panel data-testid="pipeline-code-editor">
      <PipelineEditorSurface
        cmRef={cmRef}
        pointerDownRef={pointerDownRef}
        code={code}
        setCode={setCode}
        extensions={extensions}
        editsLocked={editsLocked}
        opening={opening}
        contextItems={contextItems}
      />
      {/* The view-only studio has nothing to do in the footer — only
          copying is possible — so it goes. */}
      {!editsLocked && (
        <PipelineStatusBar
          code={code}
          wrap={wrap}
          onToggleWrap={onToggleWrap}
          onFormat={commands.formatDocument}
          runCommand={commands.runCommand}
          onClear={() => setCode("")}
          cursor={cursor}
          lintStatus={lintStatus}
          errors={errors}
          onJump={(from) => jumpToOffset(cmRef.current?.view, from)}
        />
      )}
    </Panel>
  );
}

// The pipeline editor's status bar: the shared editing controls plus the
// live lint verdict on the right.
function PipelineStatusBar({
  code,
  wrap,
  onToggleWrap,
  onFormat,
  runCommand,
  onClear,
  cursor,
  lintStatus,
  errors,
  onJump,
}: {
  code: string;
  wrap: boolean;
  onToggleWrap: () => void;
  onFormat: () => void;
  runCommand: (command: typeof indentMore) => void;
  onClear: () => void;
  cursor: { line: number; col: number };
  lintStatus: LintStatus;
  errors: EditorErrorRow[];
  onJump: (from: number) => void;
}) {
  return (
    <StatusBar>
      <StatusActions>
        <Tooltip text="Format JSON" side="top">
          <ButtonSmallSubtleIcon icon={Icon.FormatQuery} onClick={onFormat} />
        </Tooltip>
        <Tooltip text="Decrease indent" side="top">
          <ButtonSmallSubtleIcon
            icon={Icon.IndentLeft}
            onClick={() => runCommand(indentLess)}
          />
        </Tooltip>
        <Tooltip text="Increase indent" side="top">
          <ButtonSmallSubtleIcon
            icon={Icon.IndentRight}
            onClick={() => runCommand(indentMore)}
          />
        </Tooltip>
        <Tooltip
          text={wrap ? "Disable word wrap" : "Enable word wrap"}
          side="top"
        >
          <ButtonSmallSubtleIcon icon={Icon.WordWrap} onClick={onToggleWrap} />
        </Tooltip>
        <StatusDivider />
        <Tooltip text="Clear" side="top">
          <ButtonSmallSubtleIcon
            icon={Icon.Clean}
            onClick={onClear}
            disabled={code === ""}
          />
        </Tooltip>
      </StatusActions>
      {code !== "" && (
        <StatusRight data-testid="pipeline-code-status">
          <CursorReadout>
            line {cursor.line}, pos {cursor.col}
          </CursorReadout>
          <LintStatusSlot
            code={code}
            lintStatus={lintStatus}
            errors={errors}
            onJump={onJump}
          />
        </StatusRight>
      )}
    </StatusBar>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${(p) => p.theme.editorBackground};
`;

// Context-menu host around the scrolling editor region (the status bar sits
// outside it), as in the input/schema editors.
const EditorArea = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

// The empty editor's action, top-right of the surface: the ghost template
// fills the code column, so the link can't sit under line 1 like the
// input editor's sample links do. Inside the scrolled content, so it moves
// and clips with the editor. The wrapper ignores pointer events so the
// surface still focuses the editor; the link itself is clickable.
const TemplateActionOverlay = styled.div`
  position: absolute;
  top: ${CONTENT_PAD_TOP + 4}px;
  right: ${designVariables.ui.semantic.spacingm}px;
  display: flex;
  pointer-events: none;
`;

// Same scrolling recipe as the input/script editors: OverlayScrollbars owns
// the overflow, only the code region scrolls, never the status bar.
const EditorScroll = styled(OverlayScroll)`
  flex: 1;
  min-height: 0;
  ${editorRegionFocusRing}

  /* Positioned so the zero-point overlay is placed against the scrolled
     content: it scrolls with the document and is clipped by the region,
     instead of hanging out over the status bar and whatever sits below
     when the region is short. */
  [data-overlayscrollbars-contents] {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  [data-overlayscrollbars-contents] > div {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .cm-editor {
    flex: 1;
  }
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${STATUS_GAP}px;
  flex-shrink: 0;
  padding: ${STATUS_PADDING}px;
  background: ${(p) => p.theme.editorStatusBackground};
  border-top: 1px solid ${(p) => p.theme.editorStatusBorder};
`;

const StatusActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${STATUS_GAP}px;
  flex: 1;
  min-width: 0;
`;

const StatusRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${STATUS_GAP}px;
  flex-shrink: 0;
`;

const CursorReadout = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextLight};
  white-space: nowrap;
`;

const StatusIconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const LintSpinner = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid ${(p) => p.theme.semanticTextLight};
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

const StatusDivider = styled.span`
  width: 8px;
  height: 20px;
  flex-shrink: 0;
  background-image: linear-gradient(
    ${(p) => p.theme.editorStatusBorder},
    ${(p) => p.theme.editorStatusBorder}
  );
  background-size: 1px 100%;
  background-position: center;
  background-repeat: no-repeat;
`;
